import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API, toast } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Mail, RefreshCcw, Send } from 'lucide-react';
import { getAuthToken } from '@/utils/authSession';

const defaultFilters = {
  role: '',
  subscribed: '',
  source: '',
  search: '',
};

const defaultComposer = {
  subject: '',
  title: '',
  body: '',
  template_type: 'information',
  preheader: '',
  cta_label: '',
  cta_url: '',
  role: '',
  segment: '',
  source: '',
  subscribed_only: true,
  tags: '',
};

const roleOptions = [
  { value: '', label: 'Tous les rôles' },
  { value: 'client', label: 'Clients' },
  { value: 'vendeur', label: 'Vendeurs' },
  { value: 'livreur', label: 'Livreurs' },
  { value: 'relais', label: 'Relais' },
  { value: 'admin', label: 'Admins' },
  { value: 'ambassador', label: 'Ambassadeurs' },
  { value: 'newsletter_only', label: 'Newsletter uniquement' },
];

const sourceOptions = [
  { value: '', label: 'Toutes les sources' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'signup', label: 'Inscription' },
  { value: 'commande', label: 'Commande' },
  { value: 'import', label: 'Import' },
  { value: 'unsubscribe', label: 'Désinscription' },
];

const statusOptions = [
  { value: '', label: 'Tous les statuts' },
  { value: 'true', label: 'Abonnés' },
  { value: 'false', label: 'Désabonnés' },
];

const CRMNewsletterManagement = () => {
  const [contacts, setContacts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total: 0, has_next: false });
  const [filters, setFilters] = useState(defaultFilters);
  const [stats, setStats] = useState(null);
  const [composer, setComposer] = useState(defaultComposer);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadContacts(1, filters);
    loadStats();
  }, []);

  const loadContacts = async (page = pagination.page || 1, activeFilters = filters) => {
    setLoadingContacts(true);
    try {
      const token = getAuthToken();
      const params = {
        page,
        page_size: 20,
        role: activeFilters.role || undefined,
        subscribed: activeFilters.subscribed || undefined,
        source: activeFilters.source || undefined,
        search: activeFilters.search || undefined,
      };
      const response = await axios.get(`${API}/admin/newsletter/contacts`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setContacts(response.data?.items || []);
      setPagination(response.data?.pagination || { page: 1, page_size: 20, total: 0, has_next: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de charger les contacts CRM');
    } finally {
      setLoadingContacts(false);
    }
  };

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API}/admin/newsletter/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de charger les statistiques newsletter');
    } finally {
      setLoadingStats(false);
    }
  };

  const applyFilters = async () => {
    await loadContacts(1, filters);
  };

  const handleComposerChange = (key, value) => {
    setComposer((prev) => ({ ...prev, [key]: value }));
  };

  const handleSendNewsletter = async () => {
    if (!composer.subject.trim() || !composer.body.trim()) {
      toast.error('Le sujet et le contenu sont obligatoires');
      return;
    }
    setSending(true);
    try {
      const token = getAuthToken();
      const payload = {
        ...composer,
        title: composer.title || undefined,
        preheader: composer.preheader || undefined,
        cta_label: composer.cta_label || undefined,
        cta_url: composer.cta_url || undefined,
        role: composer.role || undefined,
        segment: composer.segment || undefined,
        source: composer.source || undefined,
        tags: composer.tags
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
      };
      const response = await axios.post(`${API}/admin/newsletter/send`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data?.message || 'Campagne newsletter mise en file');
      setComposer(defaultComposer);
      await loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de mettre la campagne en file');
    } finally {
      setSending(false);
    }
  };

  const preview = useMemo(() => {
    const title = composer.title.trim() || composer.subject.trim() || 'Titre newsletter';
    const lead =
      composer.template_type === 'promotion'
        ? 'Une opportunité Coli Noki à découvrir.'
        : 'Une mise à jour claire et utile pour votre activité.';
    return {
      title,
      lead,
      body: composer.body.trim() || 'Votre contenu newsletter apparaîtra ici.',
      audience: [
        composer.role ? `Rôle: ${composer.role}` : null,
        composer.source ? `Source: ${composer.source}` : null,
        composer.segment ? `Segment: ${composer.segment}` : null,
        composer.subscribed_only ? 'Abonnés uniquement' : 'Tous les contacts',
      ]
        .filter(Boolean)
        .join(' • '),
    };
  }, [composer]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">CRM / Newsletter</h2>
          <p className="text-gray-600 mt-1">
            Gérez les contacts Brevo, segmentez simplement votre audience et préparez des campagnes propres.
          </p>
        </div>
        <Button variant="outline" onClick={() => { loadContacts(pagination.page, filters); loadStats(); }}>
          <RefreshCcw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Contacts</CardDescription>
            <CardTitle>{loadingStats ? '...' : stats?.totals?.contacts ?? 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Abonnés</CardDescription>
            <CardTitle>{loadingStats ? '...' : stats?.totals?.subscribed ?? 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Désabonnés</CardDescription>
            <CardTitle>{loadingStats ? '...' : stats?.totals?.unsubscribed ?? 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Échecs d’envoi</CardDescription>
            <CardTitle>{loadingStats ? '...' : stats?.totals?.dispatch_failures ?? 0}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_1fr] gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Contacts CRM</CardTitle>
            <CardDescription>
              Vue locale des contacts synchronisés avec Brevo. Le backend reste la source de vérité métier.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="space-y-2">
                <Label htmlFor="newsletter-search">Recherche</Label>
                <Input
                  id="newsletter-search"
                  value={filters.search}
                  onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
                  placeholder="Email, nom, tag"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newsletter-role">Rôle</Label>
                <select
                  id="newsletter-role"
                  value={filters.role}
                  onChange={(event) => setFilters((prev) => ({ ...prev, role: event.target.value }))}
                  className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
                >
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="newsletter-status">Statut</Label>
                <select
                  id="newsletter-status"
                  value={filters.subscribed}
                  onChange={(event) => setFilters((prev) => ({ ...prev, subscribed: event.target.value }))}
                  className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="newsletter-source">Source</Label>
                <select
                  id="newsletter-source"
                  value={filters.source}
                  onChange={(event) => setFilters((prev) => ({ ...prev, source: event.target.value }))}
                  className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
                >
                  {sourceOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={applyFilters} disabled={loadingContacts}>
                {loadingContacts ? 'Filtrage...' : 'Appliquer les filtres'}
              </Button>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Contact</TableHead>
                    <TableHead>Rôle</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Consentement</TableHead>
                    <TableHead>Sync</TableHead>
                    <TableHead>Tags</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.map((contact) => (
                    <TableRow key={contact.id || contact.email}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-gray-900">{contact.email}</p>
                          <p className="text-sm text-gray-500">
                            {[contact.first_name, contact.last_name].filter(Boolean).join(' ') || 'Nom non renseigné'}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{contact.role || 'newsletter_only'}</Badge>
                      </TableCell>
                      <TableCell>{contact.source || '-'}</TableCell>
                      <TableCell>
                        {contact.subscribed ? (
                          <Badge className="bg-green-100 text-green-700">Abonné</Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-700">Désabonné</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="text-sm font-medium">{contact.last_sync_status || 'pending'}</p>
                        <p className="text-xs text-gray-500">{contact.last_synced_at ? new Date(contact.last_synced_at).toLocaleString('fr-FR') : 'Jamais'}</p>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(contact.tags || []).slice(0, 3).map((tag) => (
                            <Badge key={tag} variant="secondary">{tag}</Badge>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loadingContacts && contacts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                        Aucun contact ne correspond aux filtres actuels.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {pagination.total || 0} contact(s)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  disabled={(pagination.page || 1) <= 1 || loadingContacts}
                  onClick={() => loadContacts((pagination.page || 1) - 1, filters)}
                >
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  disabled={!pagination.has_next || loadingContacts}
                  onClick={() => loadContacts((pagination.page || 1) + 1, filters)}
                >
                  Suivant
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Envoyer une newsletter</CardTitle>
              <CardDescription>
                Campagne simple, mise en file via l’orchestrateur existant, puis envoyée par Brevo.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="newsletter-subject">Sujet</Label>
                  <Input id="newsletter-subject" value={composer.subject} onChange={(event) => handleComposerChange('subject', event.target.value)} placeholder="Ex: Nouveautés Coli Noki de la semaine" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newsletter-title">Titre visible</Label>
                  <Input id="newsletter-title" value={composer.title} onChange={(event) => handleComposerChange('title', event.target.value)} placeholder="Ex: Vos nouveautés logistiques" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newsletter-preheader">Preheader</Label>
                  <Input id="newsletter-preheader" value={composer.preheader} onChange={(event) => handleComposerChange('preheader', event.target.value)} placeholder="Résumé visible dans la boîte mail" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newsletter-template-type">Type</Label>
                  <select
                    id="newsletter-template-type"
                    value={composer.template_type}
                    onChange={(event) => handleComposerChange('template_type', event.target.value)}
                    className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
                  >
                    <option value="information">Information</option>
                    <option value="promotion">Promotion</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newsletter-role-filter">Audience par rôle</Label>
                  <select
                    id="newsletter-role-filter"
                    value={composer.role}
                    onChange={(event) => handleComposerChange('role', event.target.value)}
                    className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
                  >
                    {roleOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newsletter-segment">Segment</Label>
                  <Input id="newsletter-segment" value={composer.segment} onChange={(event) => handleComposerChange('segment', event.target.value)} placeholder="Ex: active, sellers, newsletter_only" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newsletter-source-filter">Source</Label>
                  <select
                    id="newsletter-source-filter"
                    value={composer.source}
                    onChange={(event) => handleComposerChange('source', event.target.value)}
                    className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
                  >
                    {sourceOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="newsletter-tags">Tags requis</Label>
                  <Input id="newsletter-tags" value={composer.tags} onChange={(event) => handleComposerChange('tags', event.target.value)} placeholder="tag1, tag2" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newsletter-cta-label">Libellé CTA</Label>
                  <Input id="newsletter-cta-label" value={composer.cta_label} onChange={(event) => handleComposerChange('cta_label', event.target.value)} placeholder="Voir la plateforme" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newsletter-cta-url">URL CTA</Label>
                  <Input id="newsletter-cta-url" value={composer.cta_url} onChange={(event) => handleComposerChange('cta_url', event.target.value)} placeholder="https://..." />
                </div>
                <div className="md:col-span-2 flex items-center justify-between rounded-lg border px-4 py-3">
                  <div>
                    <p className="font-medium text-gray-900">Limiter aux contacts abonnés</p>
                    <p className="text-sm text-gray-500">Évite d’envoyer à des contacts désabonnés.</p>
                  </div>
                  <Switch checked={composer.subscribed_only} onCheckedChange={(checked) => handleComposerChange('subscribed_only', checked)} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="newsletter-body">Contenu</Label>
                  <Textarea id="newsletter-body" rows={8} value={composer.body} onChange={(event) => handleComposerChange('body', event.target.value)} placeholder="Rédigez votre message. Chaque retour à la ligne crée un paragraphe." />
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSendNewsletter} disabled={sending}>
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? 'Mise en file...' : 'Envoyer la newsletter'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-red-600" />
                Aperçu simple
              </CardTitle>
              <CardDescription>
                Prévisualisation éditoriale avant mise en file. Le rendu email final applique le design system marketing Coli Noki.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-2xl border border-red-100 bg-gradient-to-br from-red-50 via-white to-amber-50 p-5 space-y-4">
                <Badge className="bg-red-100 text-red-700">{composer.template_type === 'promotion' ? 'Promotion' : 'Information'}</Badge>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Sujet</p>
                  <h3 className="text-xl font-semibold text-gray-900">{preview.title}</h3>
                </div>
                <p className="text-gray-700">{preview.lead}</p>
                <p className="text-sm text-gray-600 whitespace-pre-line">{preview.body}</p>
                {composer.cta_label && (
                  <div>
                    <span className="inline-flex rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white">
                      {composer.cta_label}
                    </span>
                  </div>
                )}
                <p className="text-xs text-gray-500">{preview.audience}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CRMNewsletterManagement;
